import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { IoWarningOutline, IoCheckmarkCircleOutline, IoMailOutline } from 'react-icons/io5';
import './Register.css';
import { API_URL } from '../utils/networkConfig';

const Register = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess(data.message);
        setRegistrationComplete(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Network error. Please check if the server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!form.email) {
      setError('Please enter your email address first.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess('Verification email sent! Please check your email.');
      } else {
        setError(data.error || 'Failed to resend verification email.');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show verification success message after registration
  if (registrationComplete) {
    return (
      <div className="register-container">
        <div className="register-header">
          <h2>Check Your Email</h2>
          <p>Verification Required</p>
        </div>
        
        <div className="verification-status success">
          <IoMailOutline className="status-icon info" />
          <div className="status-message">
            <h3>Registration Successful!</h3>
            <p>{success}</p>
          </div>
        </div>

        <div className="verification-actions">
          <div className="email-check-message">
            <p>
              We've sent a verification email to <strong>{form.email}</strong>
            </p>
            <p>
              Please check your email and click the verification link to activate your account.
            </p>
          </div>

          <div className="resend-section">
            <p>Didn't receive the email?</p>
            <button 
              onClick={handleResendVerification}
              disabled={loading}
              className="resend-btn"
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Sending...
                </>
              ) : (
                'Resend Verification Email'
              )}
            </button>
          </div>

          <div className="alternative-actions">
            <Link to="/login" className="login-link">
              Back to Login
            </Link>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <IoWarningOutline className="error-icon" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // Show registration form
  return (
    <div className="register-container">
      <div className="register-header">
        <h2>Create Account</h2>
        <p>Join AI Game Show and start playing!</p>
      </div>
      
      <form onSubmit={handleSubmit} className="register-form">
        <div className="form-group">
          <input
            type="text"
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>
        
        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={form.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>
        
        <div className="form-group">
          <input
            type="password"
            name="password"
            placeholder="Password (min 6 characters)"
            value={form.password}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>
        
        <button type="submit" disabled={loading} className="register-btn">
          {loading ? (
            <>
              <div className="spinner"></div>
              Creating Account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>
      
      {error && (
        <div className="error-message">
          <IoWarningOutline className="error-icon" />
          {error}
        </div>
      )}
      
      {success && !registrationComplete && (
        <div className="success-message">
          <IoCheckmarkCircleOutline className="success-icon" />
          {success}
        </div>
      )}
      
      <div className="login-redirect">
        <p>Already have an account?</p>
        <Link to="/login" className="login-link">
          Sign In
        </Link>
      </div>
    </div>
  );
};

export default Register; 